import type { ColumnType } from './table-file.service';

/**
 * Everything a new table must satisfy before any upload starts, checked the same way for a
 * single file and for every sheet of a multi-file upload. Pure, so the dialog can check all
 * sheets at once without opening each one, and so the rules are covered by specs.
 *
 * The column rules mirror what the backend refuses (`checkColumnsNameAsTypes`: reserved
 * words, `^[A-Za-z][A-Za-z0-9_]*$` on the cleaned key) plus two it would accept silently and
 * get wrong: two headers that clean to the same key (one column's values would overwrite the
 * other's), and a DATE column with no format.
 */

export type TableNameProblem = 'required' | 'too-long' | 'invalid' | 'taken' | 'duplicate';
export type ColumnProblem = 'date-format' | 'reserved' | 'empty' | 'starts-with-digit' | 'duplicate';

export interface TableProblems {
    name: TableNameProblem | null;
    /** One list per column, in column order; empty when the column is fine. */
    columns: ColumnProblem[][];
}

/** Message under the name field and in the summary. */
export const TABLE_NAME_PROBLEM_TEXT: Record<TableNameProblem, string> = {
    'required': 'Table name is required',
    'too-long': 'Max length is 63 characters',
    'invalid': 'Allowed: letters/numbers/underscore, must start with letter or underscore',
    'taken': 'Table name is already used by your account.',
    'duplicate': 'Two selected sheets use the same table name. Give each sheet its own table name.',
};

/** Tooltip on a highlighted column. */
export const COLUMN_PROBLEM_TEXT: Record<ColumnProblem, string> = {
    'date-format': 'Choose a date format for this column.',
    'reserved': 'This column name is reserved by SAI. Rename it.',
    'empty': 'This column has no usable name. Rename it using letters or numbers.',
    'starts-with-digit': 'A column name must start with a letter. Rename it.',
    'duplicate': 'Another column has the same name. Rename one of them.',
};

/** Short label used when the summary lists the columns with a problem. */
export const COLUMN_PROBLEM_LABEL: Record<ColumnProblem, string> = {
    'date-format': 'Missing date format',
    'reserved': 'Reserved column name',
    'empty': 'Column without a usable name',
    'starts-with-digit': 'Column name starts with a digit',
    'duplicate': 'Duplicate column name',
};

export interface ColumnRules {
    cleanHeaderKey(h: string): string;
    isReservedHeader(h: string): boolean;
}

const TABLE_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * The first problem with a new table's name. `existingNames` and `otherNames` are compared
 * case-insensitively; pass `existingNames` empty when the backend owns uniqueness (custom definitions).
 */
export function tableNameProblem(
    name: unknown,
    existingNames: ReadonlySet<string>,
    otherNames: readonly string[] = []
): TableNameProblem | null {
    const value = typeof name === 'string' ? name : '';
    if (!value.trim()) return 'required';
    if (value.length > 63) return 'too-long';
    if (!TABLE_NAME_RE.test(value)) return 'invalid';
    const key = value.toLowerCase();
    if (existingNames.has(key)) return 'taken';
    if (otherNames.some(n => String(n ?? '').toLowerCase() === key)) return 'duplicate';
    return null;
}

/** Problems per column. A DATE column needs a format; every name must clean to a unique, valid, unreserved key. */
export function columnProblems(
    headers: readonly string[],
    types: readonly (ColumnType | null | undefined)[],
    units: readonly (string | null | undefined)[],
    rules: ColumnRules
): ColumnProblem[][] {
    const keys = headers.map(h => rules.cleanHeaderKey(String(h ?? '')));
    const counts = new Map<string, number>();
    keys.forEach(k => counts.set(k, (counts.get(k) ?? 0) + 1));
    return headers.map((h, i) => {
        const out: ColumnProblem[] = [];
        const key = keys[i];
        if (!key) out.push('empty');
        else if (/^\d/.test(key)) out.push('starts-with-digit');
        else if (rules.isReservedHeader(String(h ?? ''))) out.push('reserved');
        if (key && (counts.get(key) ?? 0) > 1) out.push('duplicate');
        if (types[i] === 'DATE' && !units[i]) out.push('date-format');
        return out;
    });
}

export function problemCount(p: TableProblems): number {
    return (p.name ? 1 : 0) + p.columns.reduce((n, c) => n + c.length, 0);
}

/** Index of the first column with a problem, or -1. */
export function firstProblemColumn(p: Pick<TableProblems, 'columns'>): number {
    return p.columns.findIndex(c => c.length > 0);
}

function escapeHtml(s: string): string {
    return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

/**
 * The HTML the info dialog shows: one block per table with problems, its name problem and its
 * column problems grouped by kind ("Missing date format: Start, End"). Table and column names come
 * from the user's files, so they are escaped; `t` translates the fixed texts.
 */
export function describeTableProblems(
    tables: { label: string; headers: readonly string[]; problems: TableProblems }[],
    t: (key: string) => string
): string {
    return tables
        .filter(x => problemCount(x.problems) > 0)
        .map(({ label, headers, problems }) => {
            const lines: string[] = [];
            if (problems.name) lines.push(escapeHtml(t(TABLE_NAME_PROBLEM_TEXT[problems.name])));
            const byKind = new Map<ColumnProblem, string[]>();
            problems.columns.forEach((list, i) =>
                list.forEach(kind => byKind.set(kind, [...(byKind.get(kind) ?? []), String(headers[i] ?? '')]))
            );
            byKind.forEach((cols, kind) =>
                lines.push(`${escapeHtml(t(COLUMN_PROBLEM_LABEL[kind]))}: ${cols.map(c => escapeHtml(c || '?')).join(', ')}`)
            );
            return `<strong>${escapeHtml(label)}</strong><br/>${lines.join('<br/>')}`;
        })
        .join('<br/><br/>');
}
